import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import { buildRoleConfigLookup } from '@/features/auth/utils/roleConfig'
import type { RoleConfigLookup } from '@/features/auth/types/utils'
import type {
  RolesConfigCategory,
  RolesConfigResponse,
  RolesOrgData,
} from '../types/rolesConfig'

/**
 * Fetches full GET /roles payload (Angular LoadUserRoles).
 */
export async function fetchRolesConfigResponse(): Promise<RolesConfigResponse> {
  const response = await apiClient.get<RolesConfigResponse>(API_ENDPOINTS.ROLES_CONFIG)
  return response.data ?? {}
}

/**
 * Fetches role titles and category names from GET /roles (Angular LoadUserRoles).
 */
export async function fetchRolesConfig(): Promise<Record<string, RolesConfigCategory>> {
  const response = await fetchRolesConfigResponse()
  return response.role_data ?? {}
}

let rolesConfigResponseCache: RolesConfigResponse | null = null
let rolesConfigResponsePromise: Promise<RolesConfigResponse> | null = null

let rolesConfigRawCache: Record<string, RolesConfigCategory> | null = null
let rolesConfigCache: RoleConfigLookup | null = null
let rolesConfigPromise: Promise<RoleConfigLookup> | null = null

/** True after the first successful /roles load. */
export function isRolesConfigCacheReady(): boolean {
  return rolesConfigResponseCache !== null
}

/**
 * Clears the in-memory roles config cache (test-only).
 * @internal
 */
export function resetRolesConfigCacheForTests(): void {
  rolesConfigResponseCache = null
  rolesConfigResponsePromise = null
  rolesConfigRawCache = null
  rolesConfigCache = null
  rolesConfigPromise = null
}

/**
 * Cached GET /roles full response — includes org_data / captcha_details.
 */
export async function loadRolesConfigResponse(): Promise<RolesConfigResponse> {
  if (rolesConfigResponseCache) return rolesConfigResponseCache

  if (!rolesConfigResponsePromise) {
    if (import.meta.env.DEV) {
      console.debug('[referenceData] GET /roles')
    }
    rolesConfigResponsePromise = fetchRolesConfigResponse()
      .then((config) => {
        rolesConfigResponseCache = config
        rolesConfigRawCache = config.role_data ?? {}
        return config
      })
      .catch((error) => {
        rolesConfigResponsePromise = null
        throw error
      })
  }

  return rolesConfigResponsePromise
}

/**
 * Cached GET /roles role_data — mirrors Angular NgRx `LoadUserRoles` store slice.
 */
export async function loadRolesConfig(): Promise<Record<string, RolesConfigCategory>> {
  if (rolesConfigRawCache) return rolesConfigRawCache

  const response = await loadRolesConfigResponse()
  rolesConfigRawCache = response.role_data ?? {}
  return rolesConfigRawCache
}

/**
 * Cached org branding from GET /roles (`org_data.org_image` = Login Page Logo).
 */
export async function loadOrgData(): Promise<RolesOrgData> {
  try {
    const response = await loadRolesConfigResponse()
    return response.org_data ?? {}
  } catch {
    return {}
  }
}

/**
 * Cached GET /roles lookup — mirrors Angular `filter_user_roles` store.
 */
export async function loadRoleConfigLookup(): Promise<RoleConfigLookup> {
  if (rolesConfigCache) return rolesConfigCache

  if (!rolesConfigPromise) {
    rolesConfigPromise = loadRolesConfig()
      .then((config) => buildRoleConfigLookup(config))
      .then((lookup) => {
        rolesConfigCache = lookup
        return lookup
      })
      .catch(() => {
        rolesConfigPromise = null
        return { titleByRoleName: {}, typeByRoleName: {} }
      })
  }

  return rolesConfigPromise
}
